import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ArrowUp, Globe, Paperclip, Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  variant?: "hero" | "compact";
};

export function QueryInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder = "Ask anything...",
  autoFocus,
  variant = "hero",
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && window.matchMedia("(min-width: 640px)").matches) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [value]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) onSubmit();
    }
  };

  const canSubmit = value.trim().length > 0 && !disabled;

  return (
    <div
      className={cn(
        "group relative w-full rounded-2xl border border-border/70 bg-card/70 backdrop-blur",
        "shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_8px_32px_-12px_rgba(0,0,0,0.6)]",
        "transition-all focus-within:border-primary/50 focus-within:shadow-[0_0_0_1px_oklch(0.78_0.13_200/0.2),0_12px_40px_-12px_oklch(0.78_0.13_200/0.15)]",
        variant === "hero"
          ? "px-4 pt-4 pb-3 sm:px-5 sm:pt-5"
          : "px-3 pt-3 pb-2 sm:px-4",
      )}
    >
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className={cn(
          "min-h-0 resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 dark:bg-transparent",
          "placeholder:text-muted-foreground/70",
          variant === "hero"
            ? "text-base leading-relaxed sm:text-lg"
            : "text-sm leading-relaxed sm:text-base",
        )}
      />

      <div className="mt-2 flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 rounded-full border-border/60 bg-background/40 px-2.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
        >
          <Globe className="size-3.5" />
          <span>Web</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="hidden rounded-full text-muted-foreground hover:text-foreground sm:inline-flex"
        >
          <Paperclip className="size-4" />
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="hidden h-8 gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground hover:text-foreground sm:inline-flex"
          >
            <Sparkles className="size-3.5" />
            <span>auto</span>
          </Button>
          <Button
            type="button"
            onClick={() => canSubmit && onSubmit()}
            disabled={!canSubmit}
            size="icon"
            className={cn(
              "size-9 rounded-full transition-all",
              canSubmit
                ? "bg-primary text-primary-foreground shadow-[0_0_24px_-4px_oklch(0.78_0.13_200/0.5)] hover:scale-105 hover:bg-primary/90"
                : "bg-secondary text-muted-foreground hover:bg-secondary",
            )}
          >
            <ArrowUp className="size-4" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
